import { useMemo, useCallback } from "react";
import {
  MutationsObject,
  QueriesObject,
  QuestionnaireAlertReason,
} from "../context/QuestionnaireContext";

const INVALID_TOKEN_KEYWORD = "invalid token";

const isInvalidTokenMessage = (message?: string | null) => {
  if (!message) {
    return false;
  }
  return message.toLowerCase().includes(INVALID_TOKEN_KEYWORD);
};

export const useQuestionnaireError = (
  queries: QueriesObject,
  mutations: MutationsObject,
) => {
  const errorState = useMemo(() => {
    const queryError =
      queries.questionnaireQuery.error ||
      queries.disabilityCertificateQuery?.error;
    const mutationError =
      mutations.submitQuestionnaireMutation.error ||
      mutations.acceptAgreementMutation.error ||
      mutations.cancelAgreementMutation.error;

    const error = queryError || mutationError;
    const isQuery = !!queryError;

    return { error, isQuery };
  }, [
    queries.questionnaireQuery.error,
    queries.disabilityCertificateQuery?.error,
    mutations.submitQuestionnaireMutation.error,
    mutations.acceptAgreementMutation.error,
    mutations.cancelAgreementMutation.error,
  ]);

  const clearError = useCallback(() => {
    mutations.submitQuestionnaireMutation.reset();
    mutations.acceptAgreementMutation.reset();
    mutations.cancelAgreementMutation.reset();
  }, [
    mutations.submitQuestionnaireMutation,
    mutations.acceptAgreementMutation,
    mutations.cancelAgreementMutation,
  ]);

  const { shouldShowAlert, alertReason } = useMemo(() => {
    const data = queries.questionnaireQuery.data;
    const queryFinished = queries.questionnaireQuery.isFetched;
    const errorMessage = errorState.error?.message ?? null;

    let reason: QuestionnaireAlertReason = null;

    if (isInvalidTokenMessage(errorMessage)) {
      reason = "invalid_token";
    } else if (errorState.error) {
      reason = "query_error";
    } else if (!data && queryFinished) {
      reason = "no_questionnaire";
    } else if (data && !data.questionnaire?.agreement_id) {
      reason = "no_agreement";
    }

    return {
      shouldShowAlert: Boolean(reason),
      alertReason: reason,
    };
  }, [
    errorState.error,
    queries.questionnaireQuery.data,
    queries.questionnaireQuery.isFetched,
  ]);

  return { errorState, clearError, shouldShowAlert, alertReason };
};
